import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import withRouter from "../../withRouter";
import Header from "../../Component/Header";
import Menu from "../../Component/Menu";
import Footer from "../../Component/Footer";
import { currentTenant, userid } from "../../Common/Credentials";
import { spin } from "../../Common/NewLoader";
import ProjectManagementServices from "../ProjectManagementServices";

const ProjectIndenting = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'type', direction: 'asc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshData = useCallback(() => {
    setLoading(true);
    // ProjectManagementServices.GetEstimationParameters(currentTenant).then((response) => {
    //  setData(response.data)
    //  setLoading(false)
    // }).catch(() => setLoading(false))

    // For now, using dummy data to show the UI
    setTimeout(() => {
      // This is just to show the loading state
      setData([]);
      setFilteredData([]);
      setLoading(false);
    }, 1000);
  }, [currentTenant, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshData();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshData]);

  useEffect(() => {
    updateFilteredData();
  }, [data, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = data;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = data.filter(item =>
        item.type?.toLowerCase().includes(lowerSearchTerm) ||
        item.createdby?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredData);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: `Are you sure you want to delete?`,
      text: ".",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: '#152f5f',
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        ProjectManagementServices.DeleteEstimationParameters(id).then(() =>
          refreshData()
        )
      }
    })
  }

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredData.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredData.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No project indenting records found matching your search' : 'No project indenting records available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'type' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('type')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Type
                    <SortIndicator columnKey="type" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'createdby' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('createdby')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Created By
                    <SortIndicator columnKey="createdby" />
                  </div>
                </th>
                {/* {name:"type",label:"Updated At", options: { filter: false, sort: false,display: false,viewColumns: false, } }, */}
                {/* {name:"type",label:"Activity ID", options: { filter: false, sort: false,display: false,viewColumns: false, } }, */}
                {/* {name:"type",label:"Customer ID", options: { filter: false, sort: false,display: false,viewColumns: false, } }, */}
                {/* {name:"id",label:"Delivary Date", options: { filter: true, sort: true } }, */}
                {/* {name:"id",label:"Indent ID", options: { filter: false, sort: false,display: false,viewColumns: false, } }, */}
                {/* {name:"id",label:"Milestone ID", options: { filter: false, sort: false,display: false,viewColumns: false, } }, */}
                {/* {name:"preparedBy",label:"Prepared By", options: { filter: true, sort: true } }, */}
                {/* {name:"id",label:"Project ID", options: { filter: false, sort: false,display: false,viewColumns: false, } }, */}
                {/* {name:"id",label:"Project Manager ID", options: { filter: false, sort: false,display: false,viewColumns: false, } }, */}
                {/* {name:"id",label:"Remark", options: { filter: true, sort: true } }, */}
                {/* {name:"id",label:"Site Incharge", options: { filter: true, sort: true } }, */}
                {/* {name:"id",label:"Tenent Id", options: { filter: false, sort: false,display: false,viewColumns: false, } }, */}
                {/* {name:"id",label:"Updated By", options: { filter: true, sort: true } }, */}
                <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((item) => (
                <tr
                  key={item.id}
                  className={`UI-table-row ${hoveredRow === item.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                  onMouseEnter={() => setHoveredRow(item.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* Type Cell */}
                  <td
                    className={`UI-td UI-service-name ${hoveredRow === item.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.type, searchTerm)}
                  </td>

                  {/* Created By Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}
                  >
                    {highlightSearchText(item.createdby, searchTerm)}
                  </td>

                  {/* Actions Cell */}
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                    <div className="UI-action-buttons">
                      {/* Delete Button */}
                      <Tooltip title="Delete Project Indenting" arrow>
                        <button
                          className={`UI-action-button UI-delete-button ${hoveredAction === `delete-${item.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                          onClick={() => handleDelete(item.id)}
                          onMouseEnter={() => setHoveredAction(`delete-${item.id}`)}
                          onMouseLeave={() => setHoveredAction(null)}
                        >
                          <i className={`fa fa-trash UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `delete-${item.id}` ? 'UI-action-icon-hover' : ''}`} />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
          <div className="UI-card">
            {/* Header */}
            <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
              <nav className={`UI-breadcrumb ${isMobile ? 'UI-breadcrumb-mobile' : ''}`}>
                <Link
                  to="/projectmanagement"
                  className={`UI-breadcrumb-item ${hovered === 'home' ? 'UI-breadcrumb-item-hover' : ''}`}
                  onMouseEnter={() => setHovered('home')}
                  onMouseLeave={() => setHovered(null)}
                >
                  Home
                </Link>
                <span>/</span>
                <span className="UI-breadcrumb-item UI-breadcrumb-active">
                  Project Indenting
                </span>
              </nav>

              <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                <div>
                  <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                    Project Indenting
                  </h1>
                  <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                    Manage project indenting records
                  </p>
                </div>

                <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                  {/* Mobile Search */}
                  {isMobile && (
                    <input
                      type="text"
                      placeholder="Search project indenting..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="UI-mobile-search-input"
                    />
                  )}

                  <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                    {/* Desktop Search Toggle */}
                    {!isMobile && (
                      <div
                        className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                        onClick={() => {
                          if (!showSearch) setShowSearch(true);
                        }}
                        onMouseEnter={() => setHovered("search")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i
                          className="fa fa-search UI-search-icon"
                        />
                        {!showSearch && "Search"}
                        {showSearch && (
                          <input
                            type="text"
                            placeholder="Search project indenting..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="UI-search-input"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => {
                              if (searchTerm.trim() === "") {
                                setShowSearch(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* New Project Indenting button */}
                    <Link
                      to="/newprojectindenting"
                      state={{ id: -1, pagename: 'Add Project Indenting' }}
                      className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                      onMouseEnter={() => setHovered("new")}
                      onMouseLeave={() => setHovered(null)}
                    >
                      <i className="fa fa-plus" /> {isMobile ? "New" : "Add Project Indenting"}
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="UI-loading-container">
                {spin}
              </div>
            ) : (
              renderTable()
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default withRouter(ProjectIndenting);